import './grid-filter.scss';

import {
  BrokerFormSearchableSelectField,
  FormSearchableSelectField,
} from './form-searchable-select-field';
import {
  DateFormatType,
  FilterIconType,
  PopoverFieldType,
  UNDERWRITING_FILTER_OPTIONS,
} from '@converge/data';
import {
  Dispatch,
  MouseEvent,
  SetStateAction,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormControl, MenuItem, Select } from '@mui/material';
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import {
  capitalizeAndSplitWords,
  capitalizeLetterRemoveUnderLine,
  displayFormatDate,
} from '@converge/utils';
import {
  useGetEntities,
  useGetEntitiesBrokeragesOptionsList,
  useGetProgramsQuery,
  useSubmissionRenewalType,
  useSubmissionTaskType,
} from '@converge/graphql';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { CircularLoader } from './circular-loader';
import ClearIcon from '@mui/icons-material/Clear';
import { ConvergeInputLabel } from './converge-input-label';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { FormDateField } from './form-date-field';
import { FormDateRangeField } from './form-date-range-field';
import IconButton from '@mui/material/IconButton';
import { NewConvergeButton } from './new-button';
import { NewFormTextField } from './new-form-text-field';
import Popover from '@mui/material/Popover';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslation } from 'react-i18next';

export type FilterType = Record<string, any>;

export interface PopoverFields {
  label: string;
  name: string;
  type: PopoverFieldType;
  visible?: boolean;
  options?: PopoverFieldOptionType[];
  defaultValue?: string;
}

export interface PopoverFieldOptionType {
  label: string | null | undefined;
  value: string | null | undefined;
}

export type Filter = {
  nestedFilters: {
    [key: string]: any;
  };
  [key: string]: any;
};

interface GridFilterProp {
  fields: PopoverFields[];
  values: FilterType;
  icon?: FilterIconType;
  onFilterSubmit: (values: FilterType) => void;
  underWriterOptionsList?: {
    value: string | null | undefined;
    label: string;
  }[];
  underWriterAssistantOptionsList?: {
    value: string | null | undefined;
    label: string;
  }[];
}

const getFilters = (filters: any) => {
  const { nestedFilters, ...rest } = filters;
  if (nestedFilters && typeof nestedFilters === 'object') {
    return { ...rest, ...nestedFilters };
  }
  return filters;
};

const fieldIsBrokerage = (fieldLabel?: string) => {
  return fieldLabel === 'Agency' || fieldLabel === 'Brokerage';
};

export const GridFilter = (props: GridFilterProp) => {
  const { t } = useTranslation();

  const methods = useForm({
    defaultValues: getFilters(props.values),
  });

  const [internalFields, setInternalFields] = useState<PopoverFields[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [agencyData, setAgencyData] = useState('');

  const open = Boolean(anchorEl);

  const { data: renewalType, loading: renewalTypesLoading } =
    useSubmissionRenewalType({ enabled: open });

  const { data: tasks, loading: submissionTaskTypeLoading } =
    useSubmissionTaskType({
      enabled: open,
    });

  const { data: programs, loading: programsLoading } = useGetProgramsQuery({
    enabled: open,
  });

  const { entities: partners = [], loading: partnersLoading } = useGetEntities({
    isProgramPartner: true,
    enabled: open,
  });

  const { brokerages, loading: brokeragesLoading } =
    useGetEntitiesBrokeragesOptionsList({
      enabled: open,
    });

  const isFilterOptionsLoading =
    brokeragesLoading ||
    renewalTypesLoading ||
    submissionTaskTypeLoading ||
    programsLoading ||
    partnersLoading;

  const dashboardFiltersStorage = localStorage?.getItem('dashboardFilter');

  const dashboardFilters = useMemo(() => {
    return dashboardFiltersStorage?.length
      ? JSON.parse(dashboardFiltersStorage)
      : [];
  }, [dashboardFiltersStorage]);

  const RENEWAL_OPTIONS = useMemo(() => {
    return renewalType?.submissionRenewalTypes?.edges
      .map((renewal: { node: { name: string } }) => ({
        value: renewal.node.name,
        label: capitalizeLetterRemoveUnderLine(renewal.node.name),
      }))
      .sort((a: { label: string }, b: { label: string }) =>
        a.label.localeCompare(b.label)
      );
  }, [renewalType]);

  const TASK_OPTIONS = useMemo(() => {
    return tasks?.taskLabels?.edges
      ?.map((task: { node: { name: string } }) => ({
        value: task.node.name,
        label: capitalizeLetterRemoveUnderLine(task.node.name),
      }))
      .sort((a: { label: string }, b: { label: string }) =>
        a.label.localeCompare(b.label)
      );
  }, [tasks]);

  const PROGRAM_OPTIONS = useMemo(() => {
    return programs?.programs.edges.map((program) => ({
      value: program.node.uuid,
      label: program.node.name,
    }));
  }, [programs]);

  const PARTNER_OPTIONS = useMemo(() => {
    return partners
      .map((partner) => {
        return {
          value: partner?.uuid ?? '',
          label: partner?.name ?? 'N/A',
        };
      })
      ?.sort((a: { label: string }, b: { label: any }) =>
        a.label.localeCompare(b.label)
      );
  }, [partners]);

  const AGENCY_OPTION = useMemo(() => {
    return (
      (brokerages.length &&
        brokerages
          ?.map((agency) => ({
            value: agency.value,
            label: agency.label,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))) ||
      []
    );
  }, [brokerages]);

  const { underWriterOptionsList, underWriterAssistantOptionsList } = props;
  const isLoading =
    brokeragesLoading ||
    renewalTypesLoading ||
    submissionTaskTypeLoading ||
    programsLoading ||
    partnersLoading;

  const getUpdatedFields = useCallback(
    (fields: PopoverFields[]) => {
      return fields.map((obj) => {
        if (obj.name === 'agency') {
          return { ...obj, options: AGENCY_OPTION };
        } else if (obj.name === 'underwriter') {
          return { ...obj, options: underWriterOptionsList };
        } else if (obj.name === 'underwriterAssistant') {
          return { ...obj, options: underWriterAssistantOptionsList };
        } else if (obj.name === 'status') {
          return {
            ...obj,
            options: obj.options?.map((option) => ({
              value: option?.value,
              label: option?.label && capitalizeAndSplitWords(option?.label),
            })),
          };
        } else if (obj.name === 'renewalType') {
          return { ...obj, options: RENEWAL_OPTIONS };
        } else if (obj.name === 'task') {
          return { ...obj, options: TASK_OPTIONS };
        } else if (obj.name === 'program') {
          return { ...obj, options: PROGRAM_OPTIONS };
        } else if (obj.name === 'partner') {
          return { ...obj, options: PARTNER_OPTIONS };
        }
        return obj;
      });
    },
    [
      AGENCY_OPTION,
      RENEWAL_OPTIONS,
      TASK_OPTIONS,
      PROGRAM_OPTIONS,
      PARTNER_OPTIONS,
      underWriterOptionsList,
      underWriterAssistantOptionsList,
    ]
  );

  useEffect(() => {
    if (isLoading || !props.fields?.length) return;

    // Only update fields when necessary dependencies change
    setInternalFields((prevFields) => {
      const baseFields = props.fields;
      const updatedFields = getUpdatedFields(baseFields);

      // Create a new array to avoid mutating state
      const newFields = updatedFields.map((field) => ({ ...field }));

      // Update visibility based on dashboard filters
      if (dashboardFilters) {
        const nestedKeys = dashboardFilters.nestedFilters
          ? Object.keys(dashboardFilters.nestedFilters)
          : [];
        const mainKeys = Object.keys(dashboardFilters).filter(
          (key) => key !== 'nestedFilters'
        );

        [...nestedKeys, ...mainKeys].forEach((key) => {
          const fieldIndex = newFields.findIndex((f) => f.name === key);
          if (fieldIndex !== -1) {
            newFields[fieldIndex] = {
              ...newFields[fieldIndex],
              visible: true,
            };
          }
        });
      }

      // Only return new fields if they're different from previous fields
      // This prevents unnecessary re-renders
      if (JSON.stringify(prevFields) === JSON.stringify(newFields)) {
        return prevFields;
      }
      return newFields;
    });
  }, [props.fields, dashboardFilters, isLoading, open, getUpdatedFields]);

  const availableFields = useMemo(
    () => internalFields.filter((f) => !f.visible),
    [internalFields]
  );

  const isNestedFilter = (filterKey: string) =>
    [
      'agency',
      'agent',
      'underwriterAssistant',
      'underwriter',
      'task',
      'partner',
      'program',
    ].includes(filterKey);

  const handleOpenFilterPopover = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterPopoverClose = () => {
    setAnchorEl(null);
  };

  const onFilterClick = async (values: any) => {
    let filterValues = {};
    let nestedFilters = {};
    internalFields?.forEach((element) => {
      let value = values[element.name];
      if (!value) {
        return;
      }

      if (value === 'renewal') {
        value = true;
      }
      if (value === 'newBusiness') {
        value = false;
      }
      if (element.type === PopoverFieldType.DatePicker) {
        value = displayFormatDate(value, DateFormatType.ISODateFormat);
      }
      if (element.type === PopoverFieldType.DateRangePicker) {
        if (value && value[0] && value[1]) {
          value = {
            start: value[0],
            end: value[1],
          };
        } else {
          return;
        }
      }
      if (isNestedFilter(element.name)) {
        nestedFilters = { ...nestedFilters, [element.name]: value };
        filterValues = {
          ...filterValues,
          nestedFilters,
        };
      } else {
        filterValues = { ...filterValues, [element.name]: value };
      }
    });

    props.onFilterSubmit(filterValues);
    handleFilterPopoverClose();
  };

  const resetFilter = useCallback(() => {
    setInternalFields((prevFields) =>
      prevFields.map((f) => ({
        ...f,
        visible: false,
      }))
    );

    const emptyValues = internalFields.map((f) => ({
      [f.name]: getFieldDefaultValue(f),
    }));

    methods.reset(Object.assign({}, ...emptyValues));
    props.onFilterSubmit({});
    setAgencyData('');
  }, [internalFields, methods, props]);

  const onAddFilterChange = useCallback(
    (event: any) => {
      const fieldName = event.target.value;

      setInternalFields((prevFields) => {
        return prevFields.map((field) => {
          if (field.name === fieldName) {
            // If this is the field we're updating, set visible to true
            const updatedField = { ...field, visible: true };

            // Set default value if available
            if (field.defaultValue) {
              methods.setValue(field.name, field.defaultValue);
            }

            return updatedField;
          }

          return field;
        });
      });
    },
    [methods]
  );

  const onRemoveFilterClick = useCallback(
    (field: PopoverFields) => {
      setInternalFields((prevFields) => {
        return prevFields.map((f) => {
          if (f.name === field.name) {
            // If this is the field we're updating, set visible to false
            return { ...f, visible: false };
          }
          // Return other fields unchanged
          return f;
        });
      });

      if (fieldIsBrokerage(field.label)) {
        setAgencyData('');
      }

      methods.setValue(field.name, getFieldDefaultValue(field));
    },
    [methods]
  );

  const getFieldDefaultValue = (field: PopoverFields) => {
    if (field.type === PopoverFieldType.DatePicker) {
      return null;
    }
    if (field.type === PopoverFieldType.DateRangePicker) {
      return [null, null];
    }
    return '';
  };
  const clearAll = 'clearAll';
  const id = open ? 'filter-popover-id' : undefined;

  const getLabelByName = (name: string) => {
    const option = UNDERWRITING_FILTER_OPTIONS.find((opt) => opt.name === name);
    return option?.label ?? null;
  };
  const filtersList = useMemo(() => {
    const nestedFiltersList = dashboardFilters?.nestedFilters
      ? Object.keys(dashboardFilters.nestedFilters)
      : [];

    const filtersList = dashboardFilters
      ? Object.keys(dashboardFilters)
          .filter((filterKey) => filterKey !== 'nestedFilters')
          .concat(nestedFiltersList)
      : [];
    return filtersList.length ? [...filtersList, clearAll] : [];
  }, [dashboardFilters]);

  const deleteProperty = (
    mainObj: Filter,
    key: string,
    isNested: boolean
  ): Filter => {
    if (isNested) {
      if (mainObj.nestedFilters && key in mainObj.nestedFilters) {
        delete mainObj.nestedFilters[key];
      }
    } else {
      if (key in mainObj) {
        delete mainObj[key];
      }
    }

    return mainObj;
  };
  const [error, setError] = useState(false);
  return (
    <Box className="filter-wrapper">
      <div className="clear-filter-chip" data-testid="clear-filter-chip">
        {filtersList?.map((item: string, index: number) =>
          item === clearAll ? (
            <Chip
              className="filter-chip"
              label={t(`submission.${item}`)}
              onDelete={resetFilter}
            />
          ) : (
            <Chip
              className="filter-chip"
              label={
                item === 'renewal' ? 'Submission Type' : getLabelByName(item)
              }
              onDelete={() => {
                let fieldName = item;
                if (item === 'Broker') {
                  fieldName = 'agent';
                }
                if (item === 'Brokerage') {
                  fieldName = 'agency';
                }

                const internalField = internalFields.find(
                  (field) => field.name === fieldName
                );
                if (internalField) {
                  onRemoveFilterClick(internalField);

                  const newFilters = deleteProperty(
                    dashboardFilters,
                    fieldName,
                    isNestedFilter(fieldName)
                  );
                  props.onFilterSubmit(newFilters);
                }
              }}
            />
          )
        )}
      </div>
      <div>
        <IconButton
          onClick={handleOpenFilterPopover}
          aria-describedby={id}
          className="filter-icon"
          title="Filter Icon"
          data-testid="filter-icon"
        >
          <GridFilterIcon type={props.icon} />
        </IconButton>
      </div>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleFilterPopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          style: { width: '350px' },
        }}
        className="custom-popover"
      >
        <div className="popover-body">
          <FormProvider {...methods}>
            {availableFields &&
            availableFields.length > 0 &&
            !isFilterOptionsLoading ? (
              <>
                {props &&
                  internalFields?.map((f, index) => {
                    return (
                      <RenderPopoverFieldByType
                        field={f}
                        key={f.name}
                        index={index}
                        onDelete={() => onRemoveFilterClick(f)}
                        agencyData={agencyData}
                        setAgencyData={setAgencyData}
                        setError={setError}
                      />
                    );
                  })}
                <div className="add-filter-container">
                  <FormControl fullWidth size="small" variant="filled">
                    <ConvergeInputLabel
                      label={t('gridFilter.addFilterLabel', 'Add Filter')}
                    />
                    <Select
                      data-testid="grid-filter-select"
                      name="filterSelect"
                      onChange={onAddFilterChange}
                      value={''}
                      className="grid-filter-select"
                    >
                      {availableFields.map((f) => {
                        return (
                          <MenuItem
                            sx={{
                              fontWeight: '500',
                              '&:hover': {
                                backgroundColor: '#e6fdfa !important',
                              },
                              '&.Mui-selected': {
                                backgroundColor: '#e6fdfa; !important',

                                '&:hover': {
                                  backgroundColor: '#e6fdfa; !important',
                                },
                              },
                            }}
                            data-testid={`field-option-${f.name}`}
                            value={f.name}
                            key={f.name}
                          >
                            {f.label}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </div>
              </>
            ) : (
              <Box sx={{ margin: '48px' }}>
                <CircularLoader className="circular-loader-style" />
              </Box>
            )}
            <div className="footer">
              <Box className="filter-reset-btn">
                <NewConvergeButton
                  variant="outlined"
                  label="Clear"
                  onClick={resetFilter}
                />
              </Box>
              <Box>
                <NewConvergeButton
                  data-testid="filter-button"
                  label="Filter"
                  onClick={methods.handleSubmit(onFilterClick)}
                  disabled={error}
                />
              </Box>
            </div>
          </FormProvider>
        </div>
      </Popover>
    </Box>
  );
};

const RenderPopoverFieldByType = memo(
  ({
    field,
    index,
    onDelete,
    agencyData,
    setAgencyData,
    setError,
  }: {
    field: PopoverFields;
    index: number;
    onDelete: () => void;
    setError: Dispatch<SetStateAction<boolean>>;
    agencyData: string;
    setAgencyData: Dispatch<SetStateAction<string>>;
  }) => {
    // Memoize the Field component to prevent unnecessary re-renders
    const Field = useMemo(() => {
      const FieldComponent = () => {
        const { control, setValue } = useFormContext();

        const value = useWatch({
          control,
          name: field.name,
        });

        const resetField = useCallback(() => {
          setValue(field.name, '');
        }, [setValue]);

        const getValueofAgency = useCallback((item: string) => {
          if (fieldIsBrokerage(field.label)) {
            setAgencyData(item);
          }
        }, []);

        switch (field.type) {
          case PopoverFieldType.DatePicker: {
            return <FormDateField label={field.label} name={field.name} />;
          }
          case PopoverFieldType.DateRangePicker: {
            return (
              <FormDateRangeField
                label={field.label.toUpperCase()}
                name={field.name}
                setError={setError}
              />
            );
          }
          case PopoverFieldType.TextBox: {
            return (
              <NewFormTextField
                label={field.label.toUpperCase()}
                type="text"
                name={field.name}
                placeholder={`Select ${field?.name}`}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      size="small"
                      sx={{ visibility: value ? 'visible' : 'hidden' }}
                      onClick={resetField}
                      title="Clear"
                    >
                      <ClearIcon color="error" />
                    </IconButton>
                  ),
                }}
                className="filter-text-field"
              />
            );
          }
          case PopoverFieldType.Select: {
            return field?.name === 'agent' ? (
              <BrokerFormSearchableSelectField
                minDropdownWidth="270px"
                maxDropdownWidth="300px"
                brokerageUuid={agencyData}
                label={field.label.toUpperCase()}
                name={field.name}
                placeholder={`Select ${field?.label}`}
              />
            ) : (
              <FormSearchableSelectField
                minDropdownWidth="270px"
                maxDropdownWidth="300px"
                label={field?.label?.toUpperCase()}
                name={field.name}
                options={field.options}
                onChangeOption={(item) => getValueofAgency(item?.value)}
                placeholder={`Select ${field?.label}`}
              />
            );
          }
          default: {
            return null;
          }
        }
      };

      return FieldComponent;
    }, [field, setAgencyData, setError, agencyData]);

    if (!field?.visible) {
      return null;
    }

    return (
      <div className="row-wrapper">
        <div className="field-wrapper">
          <Field />
        </div>

        <div className="icon-wrapper">
          <IconButton onClick={onDelete} className="delete-icon" title="Delete">
            <DeleteIcon />
          </IconButton>
        </div>
      </div>
    );
  }
);

interface IGridFilterIconProps {
  type?: FilterIconType;
}
const GridFilterIcon = ({ type }: IGridFilterIconProps) => {
  switch (type) {
    case FilterIconType.FilledFilterIcon:
      return <FilterAltIcon className="filter-alt-icon" />;
    default:
      return <FilterAltOutlinedIcon />;
  }
};
